import {
  Edge as ReactFlowEdge,
  MarkerType,
  Node as _ReactFlowNode,
  Position,
} from 'reactflow';

import { EdgeType, NodeType } from './dagConstants';
import { DbtDagNode, QueryStatus } from './DagInterfaces';
import { DagreResult } from './DagreTypes';
import { DagMap } from './InternalDagInterfaces';

// data is a required field.
export type ReactFlowNode<TDbtDagNode extends DbtDagNode> = _ReactFlowNode<
  InteractionDagNode<TDbtDagNode>,
  NodeType
>;

export const EDGE_ID_SEPARATOR = ':::';

export const DEFAULT_REACT_EDGE_TYPE: EdgeType = 'basic';

export function stripOutUnusedEdges<TDbtDagNode extends DbtDagNode>(
  dagMap: DagMap<TDbtDagNode>,
) {
  Object.keys(dagMap).forEach(
    (key) =>
      (dagMap[key].parents = dagMap[key].parents.filter((id) => {
        return !!dagMap[id];
      })),
  );
  return dagMap;
}

export function isMatchingNode<TDbtDagNode extends DbtDagNode>(
  node: InteractionDagNode<TDbtDagNode>,
  uniqueId: string,
) {
  const { id } = node;
  return id === uniqueId;
}

/**
 * Data exposed to the user when interacting with the node
 * externally, through onInteraction events.
 */
export type InteractionDagNode<TDbtDagNode extends DbtDagNode = DbtDagNode> =
  TDbtDagNode & {
    isSelected: boolean;
    isHidden: boolean;
  };

export interface IdleNodeDataType {
  status?: QueryStatus;
}

/**
 * Detects which edges in the graph are bidirectional (have edges in both directions).
 * Returns a Set of edge IDs (in "source:::target" format) that are bidirectional.
 *
 * @param edges - Array of edges from dagre layout result
 * @returns Set of edge ID strings that represent bidirectional connections
 */
function detectBidirectionalEdges(edges: Array<{ v: string; w: string }>): Set<string> {
  const bidirectionalEdges = new Set<string>();
  const seenEdges = new Set<string>();

  edges.forEach((edge) => {
    const forward = `${edge.v}${EDGE_ID_SEPARATOR}${edge.w}`;
    const backward = `${edge.w}${EDGE_ID_SEPARATOR}${edge.v}`;

    if (seenEdges.has(backward)) {
      // Found reverse edge - mark both directions as bidirectional
      bidirectionalEdges.add(forward);
      bidirectionalEdges.add(backward);
    }
    seenEdges.add(forward);
  });

  return bidirectionalEdges;
}

export function convertFromDagreToReact<TDbtDagNode extends DbtDagNode>(
  dagreResult: DagreResult<TDbtDagNode>,
) {
  const reactFlowNodes: ReactFlowNode<TDbtDagNode>[] = [];
  const reactFlowEdgesMap = new Map<string, ReactFlowEdge>();
  const externalNodes = new Map<string, string>();
  const errorNodes = new Set<string>();

  dagreResult.nodes.forEach((node) => {
    if (!node) return;
    const {
      nodeHeight,
      nodeWidth,
      x,
      y,
      label,
      sublabel,
      annotation,
      resourceType,
      id,
      data,
      dbtCloudProject,
      projectId,
      environmentId,
      environmentDeploymentType,
      group,
      access,
      modelingLayer,
      materializationType,
      releaseVersion,
      type,
      nodeStatus,
      testStatus,
      fqn,
    } = node;

    if (node.dbtCloudProject) {
      externalNodes.set(id, node.dbtCloudProject);
    }

    if (type === 'error') {
      errorNodes.add(id);
    }

    reactFlowNodes.push({
      type,
      id,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      connectable: false,
      data: {
        ...node,
        id,
        resourceType,
        label: label || '',
        sublabel: sublabel ?? undefined,
        annotation: annotation ?? undefined,
        environmentDeploymentType: environmentDeploymentType ?? undefined,
        isSelected: false,
        isHidden: false,
        data,
        dbtCloudProject,
        projectId,
        environmentId: environmentId ?? undefined,
        group: group ?? undefined,
        access,
        modelingLayer: modelingLayer,
        materializationType,
        releaseVersion: releaseVersion ?? undefined,
        nodeStatus,
        testStatus,
        fqn: fqn ?? undefined,
      },
      position: { x, y },
      style: {
        width: nodeWidth,
        height: nodeHeight,
      },
    });
  });

  // we need this to know that an edge's target is an external project
  // so that public nodes in this project can have customized edges to it
  const nodeIdToProjectMap: Record<
    string,
    { dbtCloudProject: string; projectId: number; projectName: string }
  > = {};
  const nodeMap: Record<string, ReactFlowNode<TDbtDagNode>> = {};
  reactFlowNodes.forEach((node) => {
    if (!node || node.data?.resourceType !== 'project') {
      nodeMap[node.id] = node as ReactFlowNode<TDbtDagNode>;
    } else {
      const { dbtCloudProject, projectId, label } = node.data;
      nodeIdToProjectMap[node?.id] = { dbtCloudProject, projectId, projectName: label };
    }
  });

  // Detect bidirectional edges before processing
  const bidirectionalEdges = detectBidirectionalEdges(dagreResult.edges);

  dagreResult.edges.forEach((edge) => {
    const { v: sourceUri, w: targetUri } = edge;
    const isError = errorNodes.has(sourceUri) || errorNodes.has(targetUri);
    const targetProject = nodeIdToProjectMap[targetUri];

    // Always use parent:::child format for edge IDs (source:::target from dagre)
    // This ensures edge IDs match what getSelectedLineageEdgeIdSet creates
    const edgeKey = `${sourceUri}${EDGE_ID_SEPARATOR}${targetUri}`;
    const isBidirectional = bidirectionalEdges.has(edgeKey);

    // Skip reverse edge for bidirectional pairs — BidirectionalEdgeComponent renders both directions
    const reverseKey = `${targetUri}${EDGE_ID_SEPARATOR}${sourceUri}`;
    if (isBidirectional && reactFlowEdgesMap.has(reverseKey)) {
      return;
    }

    const existingEdge = reactFlowEdgesMap.get(edgeKey);

    if (!existingEdge) {
      // we only want to flag the external nodes that are not projects
      const isExternalNode = !!(
        nodeMap[targetUri] && nodeMap[targetUri].data?.isExternalNode
      );

      reactFlowEdgesMap.set(edgeKey, {
        type: isBidirectional ? 'bidirectional' : 'basic',
        id: edgeKey,
        source: sourceUri,
        target: targetUri,
        markerEnd: { type: MarkerType.Arrow, width: 20, height: 20 },
        data: {
          dbtCloudProject: externalNodes.get(sourceUri),
          isError,
          targetProject,
          isExternalNode,
        },
      });
    }
  });
  return { reactFlowNodes, reactFlowEdges: Array.from(reactFlowEdgesMap.values()) };
}

export function convertFromReactFlowToDagNode<TDbtDagNode extends DbtDagNode>({
  data,
}: Omit<ReactFlowNode<TDbtDagNode>, 'position'>) {
  return data;
}

export function isReactFlowNode<TDbtDagNode extends DbtDagNode>(
  element: ReactFlowNode<TDbtDagNode> | ReactFlowEdge<any>,
) {
  return Object.prototype.hasOwnProperty.call(element, 'position');
}

export function getSelectedLineageEdgeIdSet<TDbtDagNode extends DbtDagNode>(
  dagGraph: DagGraph<TDbtDagNode>,
  targetUris: string[],
): [Set<string>, Set<string>] {
  const existingUris = targetUris.filter((uri) => !!dagGraph[uri]);
  if (existingUris.length === 0) {
    return [new Set(), new Set()];
  }

  let findParents = [...existingUris];
  let findChildren = existingUris;
  const selectedEdgeIds = new Set<string>();
  const selectedNodeIds = new Set<string>();

  while (findParents.length) {
    const nextId = findParents.pop();
    if (nextId !== undefined) {
      if (selectedNodeIds.has(nextId)) {
        continue;
      }
      selectedNodeIds.add(nextId);
      dagGraph[nextId]?.parents.forEach((parentId) => {
        selectedEdgeIds.add(`${parentId}${EDGE_ID_SEPARATOR}${nextId}`);
      });
      findParents = findParents.concat(dagGraph[nextId]?.parents || []);
    }
  }

  const hasFoundChildren = new Set<string>();

  while (findChildren.length) {
    const nextId = findChildren.pop();
    if (nextId !== undefined) {
      if (hasFoundChildren.has(nextId)) {
        continue;
      }
      hasFoundChildren.add(nextId);
      selectedNodeIds.add(nextId);
      dagGraph[nextId]?.children.forEach((childId) => {
        selectedEdgeIds.add(`${nextId}${EDGE_ID_SEPARATOR}${childId}`);
      });
      findChildren = findChildren.concat(dagGraph[nextId]?.children || []);
    }
  }

  return [selectedEdgeIds, selectedNodeIds];
}

export const getNodeIdsFromEdgeId = (pathId: string) => {
  const [sourceId, targetId] = pathId.split(EDGE_ID_SEPARATOR);
  return [sourceId, targetId];
};

export type DagGraphNode<TDbtDagNode extends DbtDagNode> = TDbtDagNode & {
  children: string[];
};

export type DagGraph<TDbtDagNode extends DbtDagNode> = Record<
  string,
  DagGraphNode<TDbtDagNode>
>;

export function generateDagMap<TDbtDagNode extends DbtDagNode>(
  defaultNodeType: NodeType,
  nodes: TDbtDagNode[],
): DagMap<TDbtDagNode> {
  const dagMap: Record<string, TDbtDagNode> = {};
  nodes.forEach((node) => {
    dagMap[node.id] = {
      ...node,
      type: node.resourceType === 'error' ? 'error' : defaultNodeType,
    };
  });

  return dagMap;
}

export function generateDagGraph<TDbtDagNode extends DbtDagNode>(
  dagMap: DagMap<TDbtDagNode>,
): DagGraph<TDbtDagNode> {
  const children: Record<string, string[]> = {};
  Object.keys(dagMap).forEach((uuid) => {
    const { parents } = dagMap[uuid];
    parents.forEach((parentId: string) => {
      children[parentId] = children[parentId] || [];
      children[parentId].push(uuid);
    });
  });
  const dagGraph: DagGraph<TDbtDagNode> = {};
  Object.keys(dagMap).forEach((uuid) => {
    dagGraph[uuid] = {
      ...dagMap[uuid],
      id: uuid,
      children: children[uuid] || [],
    };
  });
  return dagGraph;
}

export function getReactNodes<TDbtDagNode extends DbtDagNode>(
  elements: (ReactFlowNode<TDbtDagNode> | ReactFlowEdge<any>)[],
): ReactFlowNode<TDbtDagNode>[] {
  return elements.filter((element) =>
    isReactFlowNode(element),
  ) as ReactFlowNode<TDbtDagNode>[];
}

export function getDagNodeStats<TDbtDagNode extends DbtDagNode>(nodes: TDbtDagNode[]) {
  let edgeCount = 0;
  nodes.forEach((node) => {
    edgeCount += node.parents.length;
  });
  return { nodeCount: nodes.length, edgeCount };
}
